import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { callMethod } from '../../../../lib/api';
import { newConversationId } from '../../../../lib/askStream';
import { deleteLocalNorthStar, saveLocalNorthStar } from '../../../../lib/clientConfig';
import { useAgentAskContext } from '../../context/AgentAskContext';
import { agentUnavailableMessage, isAgentAbortError } from '../../hooks/useAgentAsk';
import { buildAssistantContext, buildNorthStarContext } from '../../lib/agentContext';
import {
  agentScreenOf,
  contextKey,
  proposalContext,
  sameContext,
  type ThreadContext,
} from '../../lib/conversationContext';
import { askFilters, type RequestViewContext } from '../../lib/viewContext';
import type { AgentNavigateTarget } from '../../lib/situationTypes';
import type {
  AgentFocus,
  AgentScreen,
  AgentSurface,
  AskGrowthContext,
  AskHistoryTurn,
  NorthStarPayload,
  OpsCommandPerformancePayload,
  PerfSettings,
} from '../../types';
import { summarizePrompt } from '../assistant/chapterGate';
import { cardFromResponse } from './cards';
import {
  draftFromPayload,
  latestNorthStarDraft,
  northStarSummaryMessage,
  resolveAwaitingProceed,
} from './northStarFlow';
import { buildThreadHistory } from './threadHistory';
import { createThreadStore, sortThreads, type ThreadStore } from './threadStore';
import type {
  ThreadAssistantMessage,
  ThreadContextMessage,
  ThreadFlow,
  ThreadMessage,
  ThreadMeta,
} from './threadTypes';

/** What the assistant needs to know about the screen it is asked from. */
export type MaitreEnv = {
  restaurantId: string;
  regionFilter: string | null;
  scopeKey: string;
  scopeLabel: string;
  source: string[];
  shifts: string[];
  /** The status filter on screen; sent with every question (audit B6). */
  status?: string[];
  perfSettings: PerfSettings;
  /**
   * The full view context of the screen on display. A new conversation
   * records it as its own (Phase C); a conversation keeps its recorded one
   * whatever the screen shows by then, and asks once whether to use the new.
   */
  viewContext?: RequestViewContext | null;
  performance: OpsCommandPerformancePayload | null;
  screen: AgentScreen;
  surface: AgentSurface;
  /** On Growth: its figures, definitions and windows, sent with each question. */
  growth?: AskGrowthContext | null;
  existingNorthStar: NorthStarPayload | null;
  /** Show the panel (no-op in the expanded home, which shows the thread). */
  openPanel: () => void;
  closePanel: () => void;
  expand: () => void;
  collapse: () => void;
  onNavigate: (target: AgentNavigateTarget) => void;
  onNorthStarSaved: () => void;
  onNorthStarDeleted: () => void;
  store?: ThreadStore;
};

export type AskOptions = {
  focus?: AgentFocus | null;
  newThread?: boolean;
  flow?: ThreadFlow;
  /** Proceed / retry resend without a new user bubble. */
  skipUserMessage?: boolean;
  historyOverride?: AskHistoryTurn[];
};

let seq = 0;
function messageId(): string {
  seq += 1;
  return `msg_${Date.now().toString(36)}_${seq}_${Math.random().toString(36).slice(2, 6)}`;
}

function now(): string {
  return new Date().toISOString();
}

export function newThreadMeta(title: string, flow?: ThreadFlow): ThreadMeta {
  const at = now();
  return {
    id: newConversationId(),
    title,
    createdAt: at,
    updatedAt: at,
    messageCount: 0,
    ...(flow ? { flow } : {}),
  };
}

/** Meta after messages changed: counts, card ids, latest scope. */
export function touchMeta(meta: ThreadMeta, messages: ThreadMessage[]): ThreadMeta {
  const lastUser = [...messages].reverse().find(m => m.role === 'user');
  return {
    ...meta,
    updatedAt: now(),
    messageCount: messages.length,
    cardIds: messages.filter(m => m.role === 'assistant' && m.card).map(m => m.id),
    ...(lastUser && lastUser.role === 'user' && lastUser.scopeKey
      ? { scopeKey: lastUser.scopeKey, scopeLabel: lastUser.scopeLabel }
      : {}),
  };
}

/** The screen's context as a conversation records it. */
export function contextFromScreen(env: Pick<MaitreEnv, 'viewContext' | 'restaurantId'>): ThreadContext | null {
  return env.viewContext
    ? { viewContext: env.viewContext, restaurantId: env.restaurantId, recordedAt: now() }
    : null;
}

/** Where a conversation's context stands: recorded, a new chat (the screen's), or saved before contexts were recorded. */
export type ConversationContextState = 'recorded' | 'new' | 'not-recorded';

function contextMessage(kind: ThreadContextMessage['kind'], context: RequestViewContext): ThreadContextMessage {
  return { id: messageId(), role: 'context', createdAt: now(), kind, context };
}

export function useMaitreController(env: MaitreEnv) {
  const { t } = useTranslation('assistant');
  const agent = useAgentAskContext();
  const store = useMemo(() => env.store ?? createThreadStore(), [env.store]);
  const envRef = useRef(env);
  envRef.current = env;

  const [ready, setReady] = useState(false);
  const [threads, setThreads] = useState<ThreadMeta[]>([]);
  const [active, setActive] = useState<ThreadMeta | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [focus, setFocus] = useState<AgentFocus | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const activeRef = useRef<ThreadMeta | null>(null);
  const messagesRef = useRef<ThreadMessage[]>([]);
  const inFlightThreadRef = useRef<string | null>(null);
  const queuedRef = useRef<{ question: string; opts?: AskOptions } | null>(null);

  const show = useCallback((meta: ThreadMeta | null, list: ThreadMessage[]) => {
    activeRef.current = meta;
    messagesRef.current = list;
    setActive(meta);
    setMessages(list);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void store
      .listThreads()
      .then(async list => {
        if (cancelled) return;
        setThreads(list);
        const latest = list[0];
        if (latest && !activeRef.current) {
          const body = await store.loadMessages(latest.id);
          if (!cancelled && !activeRef.current) show(latest, body);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [store, show]);

  /** Append to a thread — the active one or one the operator left mid-answer. */
  const appendTo = useCallback(
    async (meta: ThreadMeta, add: ThreadMessage[]) => {
      const isActive = activeRef.current?.id === meta.id;
      const base = isActive ? messagesRef.current : await store.loadMessages(meta.id);
      const next = [...base, ...add];
      const current = isActive ? (activeRef.current ?? meta) : meta;
      const nextMeta = touchMeta(current, next);
      if (activeRef.current?.id === meta.id) show(nextMeta, next);
      setThreads(prev => sortThreads([nextMeta, ...prev.filter(x => x.id !== meta.id)]));
      try {
        await store.saveThread(nextMeta, next);
      } catch {
        // Quota or private mode: the thread still works for this session.
      }
      return nextMeta;
    },
    [store, show],
  );

  const patchActiveMeta = useCallback(
    (patch: Partial<ThreadMeta>) => {
      const meta = activeRef.current;
      if (!meta) return;
      const next = { ...meta, ...patch };
      show(next, messagesRef.current);
      if (messagesRef.current.length) {
        setThreads(prev => sortThreads([next, ...prev.filter(x => x.id !== meta.id)]));
        void store.saveThread(next, messagesRef.current).catch(() => undefined);
      }
    },
    [store, show],
  );

  const ask = useCallback(
    async (raw: string, opts: AskOptions = {}) => {
      const question = raw.trim();
      if (!question) return;
      if (agent.busy || agent.isInFlight()) {
        queuedRef.current = { question, opts };
        return;
      }
      const e = envRef.current;
      const chipFocus = opts.focus === undefined ? focus : opts.focus;
      let meta = activeRef.current;
      const before: ThreadMessage[] = [];
      if (opts.newThread || !meta) {
        // A new chat starts from the screen's context and records it (Phase C).
        const context = contextFromScreen(e);
        meta = {
          ...newThreadMeta(chipFocus?.label ?? summarizePrompt(question), opts.flow),
          ...(context ? { context } : {}),
        };
        show(meta, []);
      } else {
        if (opts.flow && meta.flow !== opts.flow) meta = { ...meta, flow: opts.flow };
        if (!meta.context && e.viewContext) {
          // Saved before contexts were recorded: from this question on it
          // counts the screen's context, and the thread says so where it starts.
          meta = { ...meta, context: contextFromScreen(e)! };
          before.push(contextMessage('recorded', e.viewContext));
        }
        if (meta !== activeRef.current) show(meta, messagesRef.current);
      }
      const prior = messagesRef.current;
      if (before.length || !opts.skipUserMessage) {
        meta = await appendTo(meta, [
          ...before,
          ...(opts.skipUserMessage
            ? []
            : [
                {
                  id: messageId(),
                  role: 'user' as const,
                  text: question,
                  createdAt: now(),
                  ...(chipFocus ? { focus: chipFocus } : {}),
                  scopeKey: e.scopeKey,
                  scopeLabel: e.scopeLabel,
                },
              ]),
        ]);
      }
      // Everything below reads the conversation's context, not the screen's:
      // its venues, period, comparison, filters, screen and window.
      const conversation = meta.context ?? null;
      const vc = conversation?.viewContext ?? e.viewContext ?? null;
      const agentScreen = vc ? agentScreenOf(vc.screen) : e.screen;
      const screenMatches = !conversation || sameContext(conversation.viewContext, e.viewContext);
      const northStarFlow = meta.flow === 'north-star';
      const dashboardContext = northStarFlow
        ? buildNorthStarContext(e.perfSettings)
        : buildAssistantContext(e.perfSettings, e.performance, {
            surface: e.surface,
            screen: agentScreen,
            focus: chipFocus,
            // The screen's Growth figures only describe the same context.
            growth: agentScreen === 'growth' && screenMatches ? e.growth : null,
          });
      const threadMeta = meta;
      inFlightThreadRef.current = threadMeta.id;
      // Filters the conversation's screen does not use never reach the tools
      // (audit 3.3); the server applies the ones it uses (audit B6).
      const filters = vc
        ? askFilters(vc.screen, { status: vc.filters.statuses, source: vc.filters.sources, shifts: vc.filters.shifts })
        : {
            source: e.source.length ? e.source : null,
            shift: e.shifts.length ? e.shifts : null,
            status: e.status?.length ? e.status : null,
          };
      try {
        const res = await agent.ask(
          {
            restaurantId: conversation?.restaurantId ?? e.restaurantId,
            question,
            regionFilter: vc?.venues.filter ?? e.regionFilter,
            source: filters.source,
            shift: filters.shift,
            status: filters.status,
            history: opts.historyOverride ?? buildThreadHistory(prior),
            dashboardContext,
            conversationId: threadMeta.id,
            ...(vc ? { viewContext: vc } : {}),
            ...(threadMeta.contextPending ? { contextMode: 'update' as const } : {}),
          },
          {
            onProgress: event => {
              if (event.type === 'mode' && event.mode === 'north-star') {
                if (activeRef.current?.id === threadMeta.id) {
                  patchActiveMeta({ flow: 'north-star' });
                }
              }
              if (event.type === 'navigate') {
                envRef.current.onNavigate({
                  kind: event.kind,
                  mode: event.mode,
                  metricKey: event.metricKey,
                  outletId: event.outletId,
                  reservationId: event.reservationId,
                  findingId: event.findingId,
                  serviceDate: event.serviceDate,
                  northStar: event.northStar,
                });
              }
            },
          },
        );
        const draft = res.northStarDraft ?? latestNorthStarDraft(prior);
        const card = cardFromResponse(res);
        const answer: ThreadAssistantMessage = {
          id: messageId(),
          role: 'assistant',
          text: res.answer,
          createdAt: now(),
          question,
          ...(card ? { card } : {}),
          ...(res.receipts?.length ? { receipts: res.receipts } : {}),
          ...(res.contextReceipt ? { contextReceipt: res.contextReceipt } : {}),
          ...(res.contextProposal ? { contextProposal: res.contextProposal } : {}),
          ...(res.conversationContext?.viewContext || vc
            ? { answerContext: res.conversationContext?.viewContext ?? vc! }
            : {}),
          ...(res.suggestedActions?.length ? { suggestedActions: res.suggestedActions } : {}),
          ...(res.northStarDraft ? { northStarDraft: res.northStarDraft } : {}),
          ...(northStarFlow || res.northStarDraft
            ? { awaitingProceed: resolveAwaitingProceed(res.answer, draft, res.awaitingProceed) }
            : {}),
          ...(res.refused ? { refused: true } : {}),
        };
        const flowMeta =
          res.northStarDraft && threadMeta.flow !== 'north-star'
            ? { ...threadMeta, flow: 'north-star' as const }
            : threadMeta;
        const base = activeRef.current?.id === threadMeta.id ? (activeRef.current ?? flowMeta) : flowMeta;
        // The server's record is the conversation's context (a change made
        // elsewhere, or one it recorded just now).
        const recorded = res.conversationContext?.viewContext;
        const withContext: ThreadMeta = recorded
          ? {
              ...base,
              context: {
                viewContext: recorded,
                restaurantId: res.conversationContext?.restaurantId ?? base.context?.restaurantId ?? e.restaurantId,
                recordedAt: res.conversationContext?.recordedAt ?? base.context?.recordedAt ?? now(),
              },
              contextPending: false,
            }
          : base;
        if (withContext !== base && activeRef.current?.id === threadMeta.id) show(withContext, messagesRef.current);
        await appendTo(withContext, [answer]);
        if (res.northStarDraft && activeRef.current?.id === threadMeta.id) {
          patchActiveMeta({ flow: 'north-star' });
        }
        agent.resetActivity();
      } catch (err) {
        agent.resetActivity();
        if (isAgentAbortError(err)) return;
        await appendTo(threadMeta, [
          {
            id: messageId(),
            role: 'assistant',
            text: agentUnavailableMessage(err),
            createdAt: now(),
            error: true,
            retryQuestion: question,
          },
        ]);
      } finally {
        if (inFlightThreadRef.current === threadMeta.id) inFlightThreadRef.current = null;
      }
    },
    [agent, appendTo, focus, patchActiveMeta, show],
  );

  // Chips and entry points asked while an answer was streaming go next.
  useEffect(() => {
    if (agent.busy || !queuedRef.current) return;
    const next = queuedRef.current;
    queuedRef.current = null;
    void ask(next.question, next.opts);
  }, [agent.busy, ask]);

  const newThread = useCallback(() => {
    show(null, []);
    setFocus(null);
  }, [show]);

  /**
   * Record a context as the conversation's from now on: on the server
   * (`opsCommand/setConversationContext`) and in the thread, where the
   * change shows. Until the server has it, the next question records it.
   */
  const recordContext = useCallback(
    async (viewContext: RequestViewContext, extra: Partial<ThreadMeta> = {}) => {
      const meta = activeRef.current;
      const e = envRef.current;
      if (!meta) return;
      const context: ThreadContext = { viewContext, restaurantId: meta.context?.restaurantId ?? e.restaurantId, recordedAt: now() };
      const next: ThreadMeta = { ...meta, context, contextPending: true, contextKeptAgainst: undefined, ...extra };
      show(next, messagesRef.current);
      await appendTo(next, [contextMessage('changed', viewContext)]);
      try {
        await callMethod('opsCommand/setConversationContext', [meta.id, viewContext, context.restaurantId]);
        if (activeRef.current?.id === meta.id) patchActiveMeta({ contextPending: false });
      } catch {
        // Offline or refused: the next question carries the change.
      }
    },
    [appendTo, patchActiveMeta, show],
  );

  /** "Use the new context": the screen's context becomes the conversation's. */
  const takeScreenContext = useCallback(async () => {
    const screen = envRef.current.viewContext;
    if (!screen || agent.busy) return;
    await recordContext(screen);
  }, [agent.busy, recordContext]);

  /** "Keep this conversation's": not asked again until the screen changes again. */
  const keepContext = useCallback(() => {
    patchActiveMeta({ contextKeptAgainst: contextKey(envRef.current.viewContext ?? null) });
  }, [patchActiveMeta]);

  /** The switch maître d'AI offered under an answer: record it, then ask the question again. */
  const switchContext = useCallback(
    async (answerId: string) => {
      const meta = activeRef.current;
      const answer = messagesRef.current.find(m => m.id === answerId);
      if (!meta?.context || !answer || answer.role !== 'assistant' || !answer.contextProposal || agent.busy) return;
      const next = proposalContext(meta.context.viewContext, answer.contextProposal);
      // The person chose this context over the screen's: no prompt about it.
      await recordContext(next, { contextKeptAgainst: contextKey(envRef.current.viewContext ?? null) });
      if (answer.question) void ask(answer.question);
    },
    [agent.busy, ask, recordContext],
  );

  /** A conversation's context, restored from the server when the thread lacks it. */
  const restoreContext = useCallback(
    async (meta: ThreadMeta) => {
      if (meta.context) return;
      try {
        const record = (await callMethod('opsCommand/getConversationContext', [meta.id])) as {
          context?: { viewContext?: RequestViewContext | null; restaurantId?: string | null; recordedAt?: string | null };
        } | null;
        const vc = record?.context?.viewContext;
        if (!vc || activeRef.current?.id !== meta.id) return;
        patchActiveMeta({
          context: {
            viewContext: vc,
            restaurantId: record?.context?.restaurantId ?? envRef.current.restaurantId,
            recordedAt: record?.context?.recordedAt ?? meta.createdAt,
          },
        });
      } catch {
        // Not recorded or unreachable: the row says so.
      }
    },
    [patchActiveMeta],
  );

  const openThread = useCallback(
    async (id: string) => {
      const meta = threads.find(x => x.id === id);
      if (!meta || activeRef.current?.id === id) return;
      const body = await store.loadMessages(id);
      show(meta, body);
      setFocus(null);
      void restoreContext(meta);
    },
    [store, threads, show, restoreContext],
  );

  /** Open the thread holding a card (Board tile → its answer). */
  const openCard = useCallback(
    async (messageIdToOpen: string) => {
      const meta = threads.find(x => x.cardIds?.includes(messageIdToOpen));
      if (!meta) return false;
      if (activeRef.current?.id !== meta.id) {
        show(meta, await store.loadMessages(meta.id));
        setFocus(null);
      }
      return true;
    },
    [store, threads, show],
  );

  const deleteThread = useCallback(
    async (id: string) => {
      if (inFlightThreadRef.current === id) agent.cancel();
      setThreads(prev => prev.filter(x => x.id !== id));
      if (activeRef.current?.id === id) newThread();
      await store.deleteThread(id).catch(() => undefined);
    },
    [agent, newThread, store],
  );

  /** "Ask about this": always the panel, a fresh thread, the focus as a chip. */
  const askAbout = useCallback(
    (about: AgentFocus, question?: string) => {
      envRef.current.openPanel();
      show(null, []);
      setFocus(about);
      if (question) void ask(question, { focus: about, newThread: true });
    },
    [ask, show],
  );

  /** "Ask maître d'AI" on the page (hero chips, Forecast, Growth, a brief
   *  row): always a fresh chat in the panel, never appended to the last one.
   *  Chips inside the panel keep using `ask` and stay in the open chat. */
  const askNew = useCallback(
    (question: string) => {
      envRef.current.openPanel();
      show(null, []);
      setFocus(null);
      void ask(question, { newThread: true, focus: null });
    },
    [ask, show],
  );

  const startNorthStar = useCallback(() => {
    const e = envRef.current;
    e.openPanel();
    setFocus(null);
    const existing = e.existingNorthStar;
    const meta = newThreadMeta(existing ? existing.label : t('ask.defineNs'), 'north-star');
    if (!existing) {
      show(meta, []);
      return;
    }
    show(meta, []);
    void appendTo(meta, [
      {
        id: messageId(),
        role: 'assistant',
        text: northStarSummaryMessage(existing),
        createdAt: now(),
        northStarDraft: draftFromPayload(existing),
      },
    ]);
  }, [appendTo, show, t]);

  const proceed = useCallback(() => {
    if (agent.busy) return;
    const ack = t('proceedAck');
    void ask(ack, {
      skipUserMessage: true,
      historyOverride: [...buildThreadHistory(messagesRef.current), { role: 'user', content: ack }],
    });
  }, [agent.busy, ask, t]);

  const retry = useCallback(
    (id: string) => {
      const failed = messagesRef.current.find(m => m.id === id);
      if (!failed || failed.role !== 'assistant' || !failed.retryQuestion || agent.busy) return;
      const meta = activeRef.current;
      const rest = messagesRef.current.filter(m => m.id !== id);
      if (meta) {
        const nextMeta = touchMeta(meta, rest);
        show(nextMeta, rest);
        void store.saveThread(nextMeta, rest).catch(() => undefined);
      }
      void ask(failed.retryQuestion, { skipUserMessage: true });
    },
    [agent.busy, ask, show, store],
  );

  const reportFailure = useCallback(
    (text: string) => {
      const meta = activeRef.current;
      if (!meta) return;
      void appendTo(meta, [{ id: messageId(), role: 'assistant', text, createdAt: now(), error: true }]);
    },
    [appendTo],
  );

  const northStarDraft = latestNorthStarDraft(messages);

  const saveNorthStar = useCallback(async () => {
    if (!northStarDraft || saving) return;
    const e = envRef.current;
    setSaving(true);
    try {
      const payload = {
        label: northStarDraft.label,
        description: northStarDraft.description,
        spec: northStarDraft.spec,
        baseline: northStarDraft.baseline,
        plan: northStarDraft.plan,
      };
      await callMethod('opsCommand/saveNorthStar', [e.restaurantId, payload]);
      saveLocalNorthStar(e.restaurantId, payload);
      e.onNorthStarSaved();
    } catch (err) {
      reportFailure(
        err instanceof Error ? t('ask.saveFailedMsg', { message: err.message }) : t('ask.saveFailed'),
      );
    } finally {
      setSaving(false);
    }
  }, [northStarDraft, reportFailure, saving, t]);

  const deleteNorthStar = useCallback(async () => {
    if (deleting) return;
    const e = envRef.current;
    setDeleting(true);
    try {
      await callMethod('opsCommand/deleteNorthStar', [e.restaurantId]);
      deleteLocalNorthStar(e.restaurantId);
      newThread();
      e.onNorthStarDeleted();
    } catch (err) {
      reportFailure(
        err instanceof Error ? t('ask.deleteFailedMsg', { message: err.message }) : t('ask.deleteFailed'),
      );
    } finally {
      setDeleting(false);
    }
  }, [deleting, newThread, reportFailure, t]);

  const lastAnswer = [...messages].reverse().find(m => m.role === 'assistant' && !m.error);
  const isNorthStarFlow = active?.flow === 'north-star';
  // The conversation's context (Phase C): a new chat shows the screen's,
  // which its first question records; one saved before then says so.
  const screenContext = env.viewContext ?? null;
  const conversationContext = active?.context?.viewContext ?? null;
  const contextState: ConversationContextState = active?.context
    ? 'recorded'
    : active && messages.length
      ? 'not-recorded'
      : 'new';
  const screenKey = contextKey(screenContext);
  const askContextChange = Boolean(
    conversationContext &&
      screenContext &&
      !sameContext(conversationContext, screenContext) &&
      active?.contextKeptAgainst !== screenKey &&
      !agent.busy,
  );

  return {
    ready,
    threads,
    activeThread: active,
    messages,
    focus,
    setFocus,
    busy: agent.busy,
    activity: agent.activity,
    cancel: agent.cancel,
    ask,
    askNew,
    askAbout,
    newThread,
    openThread,
    openCard,
    deleteThread,
    startNorthStar,
    proceed,
    retry,
    isNorthStarFlow,
    northStarDraft: isNorthStarFlow ? northStarDraft : null,
    existingNorthStar: env.existingNorthStar,
    awaitingProceed: Boolean(
      isNorthStarFlow && !agent.busy && lastAnswer?.role === 'assistant' && lastAnswer.awaitingProceed,
    ),
    saving,
    deleting,
    saveNorthStar,
    deleteNorthStar,
    surface: env.surface,
    screen: env.screen,
    screenContext,
    conversationContext,
    contextState,
    askContextChange,
    takeScreenContext,
    keepContext,
    switchContext,
    scopeKey: env.scopeKey,
    scopeLabel: env.scopeLabel,
    shifts: env.shifts,
    openPanel: env.openPanel,
    closePanel: env.closePanel,
    expand: env.expand,
    collapse: env.collapse,
  };
}

export type MaitreController = ReturnType<typeof useMaitreController>;
